import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export interface Student {
  id: string;
  name: string;
  email: string;
  phone?: string;
  course: string;
  college: string;
  status: 'active' | 'pending' | 'graduated' | 'suspended';
  joinDate: string;
  graduationDate?: string;
  guardianName?: string;
  guardianPhone?: string;
  address?: string;
  documents?: string[];
  fees?: {
    total: string;
    paid: string;
    pending: string;
  };
}

interface StudentContextType {
  students: Student[];
  addStudent: (student: Omit<Student, 'id'>) => void;
  updateStudent: (id: string, student: Partial<Student>) => void;
  deleteStudent: (id: string) => void;
  getStudentById: (id: string) => Student | undefined;
  getStudentsByCollege: (college: string) => Student[];
  getStudentsByCourse: (course: string) => Student[];
}

const StudentContext = createContext<StudentContextType | undefined>(undefined);

const STORAGE_KEY = 'edHuntWeb_students';

export const StudentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [students, setStudents] = useState<Student[]>([]);

  // Load students from localStorage on component mount
  useEffect(() => {
    const savedStudents = localStorage.getItem(STORAGE_KEY);
    if (savedStudents) {
      try {
        const parsedStudents = JSON.parse(savedStudents);
        setStudents(parsedStudents);
      } catch (error) {
        console.error('Error parsing saved students:', error);
        // If parsing fails, initialize with default students
        initializeDefaultStudents();
      }
    } else {
      // Initialize with default students if none exist
      initializeDefaultStudents();
    }
  }, []);

  // Save students to localStorage whenever students change
  useEffect(() => {
    if (students.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(students));
    }
  }, [students]);

  const initializeDefaultStudents = () => {
    const defaultStudents: Student[] = [
      {
        id: '1',
        name: 'John Doe',
        email: 'john.doe@email.com',
        phone: '+91 98765 43210',
        course: 'MBBS',
        college: 'IQ City Medical College',
        status: 'active',
        joinDate: '2024-01-15',
        guardianName: 'Robert Doe',
        guardianPhone: '+91 98765 43211',
        address: '123 Main Street, Delhi, India',
        documents: ['Admission Letter', 'Medical Certificate', 'Transfer Certificate'],
        fees: {
          total: '₹15,00,000',
          paid: '₹5,00,000',
          pending: '₹10,00,000'
        }
      },
      {
        id: '2',
        name: 'Jane Smith',
        email: 'jane.smith@email.com',
        phone: '+91 98765 43212',
        course: 'B.Tech CSE',
        college: 'Bengal Engineering College',
        status: 'active',
        joinDate: '2024-01-20',
        guardianName: 'Michael Smith',
        guardianPhone: '+91 98765 43213',
        address: '456 Tech Avenue, Kolkata, India',
        documents: ['Admission Letter', 'JEE Scorecard', '12th Marksheet'],
        fees: {
          total: '₹8,50,000',
          paid: '₹3,00,000',
          pending: '₹5,50,000'
        }
      },
      {
        id: '3',
        name: 'Priya Sharma',
        email: 'priya.sharma@email.com',
        phone: '+91 98765 43214',
        course: 'BSc Nursing',
        college: 'Medical Nursing Institute',
        status: 'pending',
        joinDate: '2024-02-01',
        guardianName: 'Raj Sharma',
        guardianPhone: '+91 98765 43215',
        address: '789 Health Lane, Mumbai, India',
        documents: ['Application Form', 'Medical Check-up'],
        fees: {
          total: '₹4,00,000',
          paid: '₹1,00,000',
          pending: '₹3,00,000'
        }
      },
      {
        id: '4',
        name: 'Arjun Patel',
        email: 'arjun.patel@email.com',
        phone: '+91 98765 43216',
        course: 'BDS',
        college: 'Dental Excellence College',
        status: 'graduated',
        joinDate: '2020-07-15',
        graduationDate: '2024-06-30',
        guardianName: 'Suresh Patel',
        guardianPhone: '+91 98765 43217',
        address: '321 Dental Street, Ahmedabad, India',
        documents: ['Degree Certificate', 'Final Transcript', 'Internship Certificate'],
        fees: {
          total: '₹12,00,000',
          paid: '₹12,00,000',
          pending: '₹0'
        }
      }
    ];
    setStudents(defaultStudents);
  };

  const addStudent = (studentData: Omit<Student, 'id'>) => {
    const newStudent: Student = {
      ...studentData,
      id: Date.now().toString(), // Simple ID generation
    };
    setStudents(prev => [...prev, newStudent]);
  };

  const updateStudent = (id: string, studentData: Partial<Student>) => {
    setStudents(prev => 
      prev.map(student => 
        student.id === id 
          ? { ...student, ...studentData, id } // Ensure ID is preserved
          : student
      )
    );
  };

  const deleteStudent = (id: string) => {
    setStudents(prev => prev.filter(student => student.id !== id));
  };

  const getStudentById = (id: string): Student | undefined => {
    return students.find(student => student.id === id);
  };

  const getStudentsByCollege = (college: string): Student[] => {
    return students.filter(student => student.college === college);
  };

  const getStudentsByCourse = (course: string): Student[] => {
    return students.filter(student => student.course === course);
  };

  return (
    <StudentContext.Provider value={{
      students,
      addStudent,
      updateStudent,
      deleteStudent,
      getStudentById,
      getStudentsByCollege,
      getStudentsByCourse
    }}>
      {children}
    </StudentContext.Provider>
  );
};

export const useStudents = () => {
  const context = useContext(StudentContext);
  if (context === undefined) {
    throw new Error('useStudents must be used within a StudentProvider');
  }
  return context;
};
